import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  AlertTriangle,
  TrendingUp,
  Users,
  MapPin,
  BarChart3,
  CheckCircle,
  Zap,
  Shield,
  Target,
  Bell,
  Layers,
  Globe,
} from "lucide-react";
import { useEffect } from "react";

const PoliticalCampaignsLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for Political Campaigns & Elected Officials - Real-Time Voter Sentiment Platform";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[105px] md:pb-24 overflow-hidden bg-gradient-to-br from-red-600 via-purple-600 to-blue-600">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-26-1920x480.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/90 via-purple-600/85 to-blue-600/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Target size={16} className="mr-2" />
                    <span>Trusted by Winning Campaigns</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-nation-teal">
                        Political Campaigns
                      </span>{" "}
                      &{" "}
                      <span className="text-yellow-300">Elected Officials</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Refine your messaging and engage voters with real-time,
                      geo-targeted sentiment insights.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-nation-teal">
                      Win with data-driven campaign strategies
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-nation-teal hover:bg-nation-teal-dark text-white h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href =
                          "/solutions/political-campaigns")
                      }
                    >
                      Request a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Download Case Studies
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>25+ Countries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 size={16} />
                      <span>Real-time Analytics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>Live Data Stream</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                          <BarChart3 size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Campaign Sentiment
                          </div>
                          <div className="text-white/60 text-sm">
                            Political Dashboard
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Charts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          District A
                        </div>
                        <div className="text-green-400 text-2xl font-bold">
                          +12%
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          vs last week
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          District B
                        </div>
                        <div className="text-blue-400 text-2xl font-bold">
                          68%
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          approval rate
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Recent Activity
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Healthcare policy +15%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                          <span>Campaign momentum +8%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span>Voter engagement +22%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating verification indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Verified Voters</div>
                  <div className="text-nation-teal font-bold">99.7%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Traditional polling methods leave campaigns vulnerable to
                surprises
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Uncertain Voter Reaction
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Unsure how key messages will resonate with different voter
                    segments
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Users className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Poor Demographic Segmentation
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Difficulty understanding opinion differences by region or
                    demographic group
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <TrendingUp className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Costly, Infrequent Polls
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Over-reliance on expensive traditional polls that are
                    outdated by the time results arrive
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Shield className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Data Manipulation Risk
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Vulnerability to online manipulation and fake responses
                    skewing your data
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How We Help You Win
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Turn real-time voter insights into winning campaign strategies
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
                    <MapPin className="h-8 w-8 text-red-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Geo-Targeted Sentiment Scores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Track daily shifts in voter mood across districts,
                    precincts, and key demographic segments with precision
                    mapping.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Interactive Issue Polls
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Deploy quick, branded surveys on key issues and analyze
                    responses in minutes, not weeks.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                    <Shield className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Voter Verification Engine
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Ensure each response is from a real, unique voter—no bots,
                    no duplicates, no manipulation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Key Benefits & Capabilities
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 p-4 rounded-lg light-shadow-base">
                <Zap className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                <span className="font-medium">
                  Live sentiment during rallies & debates
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 p-4 rounded-lg light-shadow-base">
                <Bell className="h-6 w-6 text-red-600 flex-shrink-0" />
                <span className="font-medium">
                  Custom alerts for sentiment spikes
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 p-4 rounded-lg light-shadow-base">
                <Layers className="h-6 w-6 text-blue-600 flex-shrink-0" />
                <span className="font-medium">
                  Segment by age, gender, geography
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 p-4 rounded-lg light-shadow-base">
                <Target className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">
                  Easy API integration with campaign tools
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Success Story */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-red-50 to-blue-50 dark:from-red-950/20 dark:to-blue-950/20 border-red-200 dark:border-red-800/50">
              <CardHeader>
                <div className="text-center">
                  <Badge className="mb-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                    Campaign Success Story
                  </Badge>
                  <CardTitle className="text-2xl md:text-3xl text-nation-blue">
                    Sen. Doe's Re-Election: Data-Driven Strategy
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      15%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Boost in turnout in swing districts
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      5
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Major policy announcements with real-time response
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600 mb-2">
                      72h
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Average response time to sentiment shifts
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-950/30"
                  >
                    Read Full Case Study
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-nation-blue mb-8">
                Trusted by Winning Campaigns Worldwide
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60 mb-12">
                <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded flex items-center justify-center text-sm font-medium text-gray-800 dark:text-gray-200">
                  Victory Campaign 2024
                </div>
                <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded flex items-center justify-center text-sm font-medium text-gray-800 dark:text-gray-200">
                  Progressive Action PAC
                </div>
                <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded flex items-center justify-center text-sm font-medium text-gray-800 dark:text-gray-200">
                  Citizens United Victory
                </div>
                <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded flex items-center justify-center text-sm font-medium text-gray-800 dark:text-gray-200">
                  Future Leaders Initiative
                </div>
              </div>

              <Card className="max-w-2xl mx-auto">
                <CardContent className="pt-8">
                  <blockquote className="text-lg italic text-muted-foreground mb-6">
                    "NationCard™ gave us the edge we needed. Real-time
                    sentiment tracking helped us pivot our messaging 48 hours
                    before election day, resulting in a decisive victory in
                    three key swing districts."
                  </blockquote>
                  <div className="text-center">
                    <div className="font-semibold">Maria Rodriguez</div>
                    <div className="text-sm text-muted-foreground">
                      Campaign Manager
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Rodriguez for Senate 2024
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-red-600 via-purple-600 to-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Winning with NationCard™
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to gain the competitive edge? See how real-time voter
              insights can transform your campaign strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-red-600 hover:bg-white/90"
                onClick={() => (window.location.href = "/contact")}
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10"
              >
                Schedule Strategy Session
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default PoliticalCampaignsLanding;
